import React from 'react';
import { Badge, Box, Button, Group, List, Modal, Stack, Text, Title } from '@mantine/core';
import { IconBulb } from '@tabler/icons-react';
import type { FeatureTip } from './featureTipsCatalog';

export interface FeatureTipModalProps {
  opened: boolean;
  tip: FeatureTip | null;
  /** When true, primary action is “Got it” (dismiss). Hub browse can use Close only. */
  showDismiss: boolean;
  onClose: () => void;
  onDismiss: () => void;
}

export function FeatureTipModal({
  opened,
  tip,
  showDismiss,
  onClose,
  onDismiss,
}: FeatureTipModalProps) {
  if (!tip) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap={8} wrap="nowrap" align="center">
          <IconBulb size={18} stroke={1.75} color="var(--mantine-color-brand-6)" />
          <Text fw={700} size="sm">
            {tip.title}
          </Text>
          {showDismiss ? (
            <Badge size="xs" variant="light" color="brand">
              New
            </Badge>
          ) : null}
        </Group>
      }
      centered
      size="md"
      radius="md"
      padding="lg"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed" style={{ lineHeight: 1.5 }}>
          {tip.summary}
        </Text>

        {tip.benefits.length > 0 ? (
          <Box>
            <Title order={6} mb={6}>
              Why it helps
            </Title>
            <List size="sm" spacing={6} withPadding>
              {tip.benefits.map((item) => (
                <List.Item key={item}>{item}</List.Item>
              ))}
            </List>
          </Box>
        ) : null}

        {tip.howTo && tip.howTo.length > 0 ? (
          <Box>
            <Title order={6} mb={6}>
              How to use it
            </Title>
            <List size="sm" spacing={6} type="ordered" withPadding>
              {tip.howTo.map((item) => (
                <List.Item key={item}>{item}</List.Item>
              ))}
            </List>
          </Box>
        ) : null}

        <Group justify="flex-end" gap="sm" mt={4}>
          {showDismiss ? (
            <>
              <Button variant="default" onClick={onClose}>
                Close
              </Button>
              <Button color="brand" onClick={onDismiss}>
                Got it
              </Button>
            </>
          ) : (
            <Button color="brand" onClick={onClose}>
              Close
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
